import { ReactNode, useCallback, useMemo, useState } from "react";
import { AvailableActivity } from "@/lib/api/activities";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePostHogEvent } from "@/lib/posthog";
import { FireIcon } from "@heroicons/react/24/outline";
import { Gamepad, HeartIcon, MonitorPlay } from "lucide-react";
import { useFeatureFlagEnabled } from "@posthog/react";
import { useActivitySendController } from "./use-activity-send-controller";
import {
  getActivityUnavailableTooltip,
  useAvailableActivityItems,
} from "./use-available-activity-items";

interface ActivityTriggerProps {
  mentorshipId: string;
  teenName: string;
}

interface ActivityItem extends AvailableActivity {
  icon: ReactNode;
}

export function ActivityTrigger({
  mentorshipId,
  teenName,
}: ActivityTriggerProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const { dispatchEvent } = usePostHogEvent("activity.mentor");
  const functionalMomentsEnabled =
    useFeatureFlagEnabled("functional-moments") ||
    import.meta.env.DEV ||
    import.meta.env.VITE_STYTCH_PUBLIC_ENV !== "production";

  const { items } = useAvailableActivityItems(mentorshipId);
  const { selectActivity, dialogs } = useActivitySendController({
    mentorshipId,
    teenName,
  });

  const activities = useMemo<ActivityItem[]>(
    () =>
      items.map((activity) => {
        let icon: ReactNode = (
          <MonitorPlay className="h-5 w-5 shrink-0 text-zinc-600" />
        );
        if (activity.type === "hot_takes") {
          icon = <FireIcon className="h-5 w-5 shrink-0 text-zinc-600" />;
        }
        if (activity.type === "deep_thoughts") {
          icon = (
            <HeartIcon
              strokeWidth={1.5}
              className="h-5 w-5 shrink-0 text-zinc-600"
            />
          );
        }
        return { ...activity, icon };
      }),
    [items],
  );

  const handleTriggerClick = useCallback(() => {
    dispatchEvent("activity.mentor.tab_opened", { mentorshipId });
  }, [dispatchEvent, mentorshipId]);

  const handleSelectActivity = useCallback(
    (activity: AvailableActivity) => {
      setPopoverOpen(false);
      selectActivity(activity);
    },
    [selectActivity],
  );

  if (!functionalMomentsEnabled) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="ml-2 gap-2"
            onClick={handleTriggerClick}
            title="Send an activity"
          >
            <Gamepad className="h-4 w-4" />
            Engagement
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-52 rounded-2xl border-[#dfdad3] p-2 shadow-xl"
          align="end"
          side="bottom"
        >
          <div className="flex flex-col gap-1">
            {activities.map((activity) => {
              const tooltipText = getActivityUnavailableTooltip(activity);

              const row = (
                <button
                  key={activity.type}
                  onClick={() => handleSelectActivity(activity)}
                  disabled={!activity.enabled || !activity.isTeenEligible}
                  className="flex w-full items-center gap-2 rounded-xl px-4 py-3 text-left text-base text-text-primary transition-colors hover:bg-surfaces-z3 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {activity.icon}
                  <span className="truncate">{activity.label}</span>
                </button>
              );

              if (tooltipText) {
                return (
                  <Tooltip key={activity.type}>
                    <TooltipTrigger asChild>
                      <div>{row}</div>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      {tooltipText}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return row;
            })}
            {!activities.length && (
              <p className="py-2 text-center text-sm text-zinc-400">
                No activities right now - check back soon.
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {dialogs}
    </TooltipProvider>
  );
}
